import React, { useState, useEffect } from 'react';
import {
  Card,
  Avatar,
  Typography,
  Row,
  Col,
  Space,
  Descriptions,
  Tag,
  Button,
  Upload,
  message,
  Spin,
  Divider,
  Form,
  Input,
  Switch,
  Modal
} from 'antd';
import {
  UserOutlined,
  MailOutlined,
  DatabaseOutlined,
  ServerOutlined,
  HotelOutlined,
  SettingOutlined,
  EditOutlined,
  CameraOutlined,
  SaveOutlined,
  LockOutlined,
  DeleteOutlined,MoonOutlined,  // Añade este
  SunOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { BASE_URL } from '../utils/constants';

import { ThemeProvider, useTheme } from '../services/ThemeContext';

const { Title, Text } = Typography;

const UserProfileScreen = ({ onClose }) => {
  const { toggleDarkMode, isDarkMode } = useTheme();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editableUser, setEditableUser] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BASE_URL}/api/usuarios/profile`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setUser(response.data.user);
        setEditableUser({...response.data.user});
        form.setFieldsValue(response.data.user);
      } else {
        message.error('Error al cargar el perfil');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpload = async (file) => {
    try {
      setUploadingAvatar(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('avatar', file);
      
      const response = await axios.post(
        `${BASE_URL}/api/usuarios/avatar`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data.success) {
        message.success('Avatar actualizado');
        setUser(response.data.user);
        setEditableUser({...response.data.user});
        // Actualizar localStorage
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
    } catch (error) {
      console.error('Error al subir avatar:', error);
      message.error(error.response?.data?.error || 'Error al subir avatar');
    } finally {
      setUploadingAvatar(false);
    }
    
    return false;
  };

  const handleRemoveAvatar = async () => {
    Modal.confirm({
      title: 'Eliminar avatar',
      content: '¿Estás seguro de que quieres eliminar tu avatar?',
      okText: 'Sí, eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.delete(
            `${BASE_URL}/api/usuarios/avatar`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          if (response.data.success) {
            message.success('Avatar eliminado');
            setUser(response.data.user);
            setEditableUser({...response.data.user});
            localStorage.setItem('user', JSON.stringify(response.data.user));
          }
        } catch (error) {
          console.error('Error eliminando avatar:', error);
          message.error('Error al eliminar avatar');
        }
      }
    });
  };

  const handleSaveChanges = async (values) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${BASE_URL}/api/usuarios/update`,
        values,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        message.success('Perfil actualizado correctamente');
        setIsEditing(false);
        setUser(response.data.user);
        setEditableUser({...response.data.user});
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        if (onClose) {
          setTimeout(onClose, 1000); // Cerrar modal después de 1 segundo
        }
      }
    } catch (error) {
      console.error('Error al actualizar:', error);
      message.error(error.response?.data?.error || 'Error al actualizar perfil');
    }
  };

  const renderPermissions = () => {
    if (!user) return null;
    
    const permissions = [
      { key: 'Maestros', value: user.Maestros, label: 'Maestros', color: 'blue' },
      { key: 'Reservas', value: user.Reservas, label: 'Reservas', color: 'green' },
      { key: 'Hoteles', value: user.Hoteles, label: 'Hoteles', color: 'orange' },
      { key: 'planing', value: user.planing, label: 'Planing', color: 'purple' },
      { key: 'PickUp', value: user.PickUp, label: 'PickUp', color: 'cyan' },
      { key: 'crm', value: user.crm, label: 'CRM', color: 'magenta' },
      { key: 'EsCentral', value: user.EsCentral, label: 'Central', color: 'gold' },
    ];

    return permissions
      .filter(perm => perm.value === 1)
      .map(perm => (
        <Tag key={perm.key} color={perm.color}>
          {perm.label}
        </Tag>
      ));
  };

  const renderAvatar = () => (
    <div style={{ textAlign: 'center' }}>
      <Upload
        showUploadList={false}
        beforeUpload={handleAvatarUpload}
        accept="image/*"
        disabled={uploadingAvatar}
      >
        <Spin spinning={uploadingAvatar}>
          <Avatar
            size={120}
            src={user?.avatar ? `${BASE_URL}${user.avatar}` : null}
            icon={!user?.avatar && <UserOutlined />}
            style={{
              backgroundColor: user?.avatar ? 'transparent' : '#1890ff',
              fontSize: '48px',
              cursor: 'pointer',
              border: '3px solid #f0f0f0'
            }}
          />
        </Spin>
      </Upload>
      
      <div style={{ marginTop: 10, display: 'flex', justifyContent: 'center', gap: 8 }}>
        <Upload
          showUploadList={false}
          beforeUpload={handleAvatarUpload}
          accept="image/*"
          disabled={uploadingAvatar}
        >
          <Button 
            icon={<CameraOutlined />} 
            size="small"
            type="text"
            loading={uploadingAvatar}
          >
            Cambiar
          </Button>
        </Upload>
        
        {user?.avatar && (
          <Button 
            icon={<DeleteOutlined />} 
            size="small"
            type="text"
            danger
            onClick={handleRemoveAvatar}
          >
            Eliminar
          </Button>
        )}
      </div>
    </div>
  );

const handleToggleDarkMode = async (checked) => {
  // Actualizar en backend
  try {
    const token = localStorage.getItem('token');
    const response = await axios.put(
      `${BASE_URL}/api/usuarios/actualiza/modo-oscuro`,
      { modoOscuro: checked ? 1 : 0 },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    if (response.data.success) {
      // Actualizar UI inmediatamente sin recargar
      toggleDarkMode(checked);
      message.success(`Modo ${checked ? 'oscuro' : 'claro'} activado`);
      setUser(response.data.user);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
  } catch (error) {
    message.error('Error al cambiar preferencia de tema');
  }
};

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <Spin size="large" tip="Cargando perfil..." />
      </div>
    );
  }

  if (!user) {
    return (
      <Card>
        <Typography.Text type="danger">
          No se pudo cargar la información del usuario
        </Typography.Text>
      </Card>
    );
  }

  return (
    <div style={{ padding: '8px' }}>
      <Row gutter={[24, 24]} justify="center">
        <Col xs={24} md={8}>
          <Card>
            {renderAvatar()}
            
            <Divider />
            
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <Title level={4} style={{ marginBottom: 4 }}>
                {user.nombre}
              </Title>
              <Text type="secondary">
                <MailOutlined /> {user.usuario}
              </Text>
            </div>
          </Card>
        </Col>
        
        <Col xs={24} md={16}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSaveChanges}
            initialValues={user}
          >
            <Card 
              title="Información del Usuario" 
              style={{ marginBottom: 24 }}
              extra={
                !isEditing ? (
                  <Button 
                    type="primary" 
                    icon={<EditOutlined />}
                    onClick={() => setIsEditing(true)}
                  >
                    Editar
                  </Button>
                ) : (
                  <Space>
                    <Button onClick={() => setIsEditing(false)}>
                      Cancelar
                    </Button>
                    <Button 
                      type="primary" 
                      icon={<SaveOutlined />}
                      htmlType="submit"
                    >
                      Guardar
                    </Button>
                  </Space>
                )
              }
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="nombre"
                    label="Nombre"
                    rules={[{ required: true, message: 'Por favor ingresa tu nombre' }]}
                  >
                    <Input 
                      disabled={!isEditing}
                      placeholder="Tu nombre completo"
                    />
                  </Form.Item>
                </Col>
                
                <Col span={12}>
                  <Form.Item
                    name="UsuarioCorto"
                    label="Usuario Corto"
                    rules={[{ required: true, message: 'Por favor ingresa un usuario corto' }]}
                  >
                    <Input 
                      disabled={!isEditing}
                      placeholder="Usuario corto"
                    />
                  </Form.Item>
                </Col>
              </Row>
              
              <Form.Item
                name="usuario"
                label="Correo Electrónico"
                rules={[
                  { required: true, message: 'Por favor ingresa tu correo' },
                  { type: 'email', message: 'Ingresa un correo válido' }
                ]}
              >
                <Input 
                  disabled={!isEditing}
                  placeholder="correo@ejemplo.com"
                />
              </Form.Item>
              
              {isEditing && user.Firma !== undefined && (
                <Form.Item
                  name="Firma"
                  label="Firma Digital"
                >
                  <Input.TextArea 
                    rows={3}
                    placeholder="Firma digital o notas adicionales"
                  />
                </Form.Item>
              )}
              
              {!isEditing && (
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Rol">
                    <Tag color={user?.esmaster === 1 ? 'red' : 'blue'}>
                      {user?.esmaster === 1 ? 'Administrador' : 'Usuario'}
                    </Tag>
                  </Descriptions.Item>
                  
                  <Descriptions.Item label="Código Hotel">
                    {user?.CodigoHotel}
                  </Descriptions.Item>
                  
                  <Descriptions.Item label="Último Hotel">
                    {user?.ultimoHotel}
                  </Descriptions.Item>
                  
                  <Descriptions.Item label="ID Usuario">
                    <Text code copyable>
                      {user.id}
                    </Text>
                  </Descriptions.Item>
                </Descriptions>
              )}
            </Card>
          </Form>
          
          <Card 
            title={
              <Space>
                <SettingOutlined />
                <span>Preferencias</span>
              </Space>
            }
            style={{ marginTop: 24 }}
          >
            <Row align="middle" justify="space-between">
              <Col>
                <Space>
                  {user?.modoOscuro === 1 ? <MoonOutlined /> : <SunOutlined />}
                  <span>
                    <Text strong>Modo Oscuro</Text>
                    <br />
                    <Text type="secondary">
                      Activar tema oscuro para toda la aplicación
                    </Text>
                  </span>
                </Space>
              </Col>
              <Col>
                <Switch
                  checked={user?.modoOscuro === 1}
                  onChange={handleToggleDarkMode}
                  checkedChildren={<MoonOutlined />}
                  unCheckedChildren={<SunOutlined />}
                />
              </Col>
            </Row>
          </Card>

          {/* Sección de permisos (solo lectura) */}
          <Card 
            title={
              <Space>
                <SettingOutlined />
                <span>Permisos y Accesos</span>
              </Space>
            }
          >
            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Módulos activos:
              </Text>
              <Space wrap>
                {renderPermissions()}
              </Space>
            </div>
            
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Auto Código">
                <Tag color="purple">{user?.AutoCodigo}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Usuario Amphitrion">
                <Text code>{user?.UsuarioAnphitrion}</Text>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default UserProfileScreen;